const nodeEnv = process.env.NODE_ENV
const freeTechEnv = process.env.VITE_FREETECH_ENV
const viteApiUrl = process.env.VITE_API_URL?.trim()
const firebaseProjectId = process.env.VITE_FIREBASE_PROJECT_ID || ''

let functionsEnv: string = nodeEnv || 'development'

if (window.location.hostname === 'localhost') {
  if (freeTechEnv === 'staging') {
    functionsEnv = 'test'
  } else {
    functionsEnv = 'development'
  }
} else if (
  firebaseProjectId === 'otw-production-91ffb' ||
  firebaseProjectId.includes('production')
) {
  // Prod-bake PR previews host on *.web.app with "stg" in the site id — prefer Firebase project.
  functionsEnv = 'production'
} else if (window.location.hostname.includes('stg')) {
  functionsEnv = 'staging'
} else {
  functionsEnv = 'production'
}

// Define base URLs for each environment
const functionsUrls = {
  production: 'https://us-central1-otw-production-91ffb.cloudfunctions.net',
  staging: 'https://us-central1-otw-stg.cloudfunctions.net',
  development: 'http://127.0.0.1:5001/otw-production-91ffb/us-central1',
  test: 'http://127.0.0.1:5001/otw-stg/us-central1',
}

import {
  PROD_API_CLOUD_RUN_ORIGIN,
  PROD_API_ORIGIN,
  STG_API_CLOUD_RUN_ORIGIN,
  STG_API_ORIGIN,
} from './apiOrigins'

export { PROD_API_ORIGIN, STG_API_ORIGIN, PROD_API_CLOUD_RUN_ORIGIN, STG_API_CLOUD_RUN_ORIGIN }

const PROD_API_BASE = `${PROD_API_CLOUD_RUN_ORIGIN}/api/v1`
const STG_API_BASE = `${STG_API_CLOUD_RUN_ORIGIN}/api/v1`

const envUrls = {
  production: PROD_API_BASE,
  staging: STG_API_BASE,
  development: 'http://localhost:8080/api/v1',
  test: 'http://localhost:8080/api/v1',
}

/** Accept `…/api`, `…/api/v1`, or a bare origin from VITE_API_URL. */
const normalizeApiBase = (url: string): string => {
  const cleaned = url.replace(/\/$/, '')
  if (cleaned.endsWith('/api/v1')) return cleaned
  if (cleaned.endsWith('/api')) return `${cleaned}/v1`
  return `${cleaned}/api/v1`
}

const isLegacyCloudRunApiHost = (hostname: string): boolean =>
  /^otw-api-(prod|stg)-/.test(hostname) && hostname.endsWith('.run.app')

/**
 * Route browser API traffic to Cloud Run. Custom api./dev-api. hostnames and legacy
 * env URLs all normalize to the same run.app base (custom domain proxy is optional).
 */
export const canonicalizeApiBase = (url: string): string => {
  const normalized = normalizeApiBase(url)
  try {
    const { hostname } = new URL(normalized)
    if (hostname === 'api.optictruckworks.com' || hostname === 'dev-api.optictruckworks.com') {
      return hostname === 'dev-api.optictruckworks.com' ? STG_API_BASE : PROD_API_BASE
    }
    if (isLegacyCloudRunApiHost(hostname)) {
      return hostname.includes('stg') ? STG_API_BASE : PROD_API_BASE
    }
  } catch {
    /* ignore */
  }
  return normalized
}

const isLoopbackHost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1'

/**
 * Prefer VITE_API_URL (prod-bake / env switch). Fall back to hostname/project heuristics.
 * Loopback always uses the local API. Production Cloud Run does not allow
 * http://localhost CORS, so a prod VITE_API_URL cannot serve `pnpm dev`.
 */
const remoteApiBase = viteApiUrl
  ? canonicalizeApiBase(viteApiUrl)
  : envUrls[functionsEnv as keyof typeof envUrls] || envUrls.development

export const apiBaseUrl =
  typeof window !== 'undefined' && isLoopbackHost(window.location.hostname)
    ? envUrls.development
    : remoteApiBase

export const firebaseFunctionsBaseUrl =
  functionsUrls[functionsEnv as keyof typeof functionsUrls] || functionsUrls.development
